import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const { formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // "email" | "reset"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If that email is registered, a reset code is on its way");
      setStep("reset");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) return toast.error("New password must be at least 8 characters");
    if (newPassword !== confirmPassword) return toast.error("New passwords don't match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code: code.trim(), new_password: newPassword });
      toast.success("Password reset — log in with your new password");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#151613] text-[#f1e0c5] flex items-center justify-center px-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="font-display text-2xl flex items-center justify-center gap-2.5 mb-10">
          <img src="/logo192.png" alt="Call to Arms" className="w-8 h-8 rounded-md" />
          Call to Arms<span className="text-[#D90429]">.</span>
        </Link>
        <div className="panel-wood rounded-lg p-8">
          {step === "email" ? (
            <>
              <h1 className="font-display text-3xl mb-2">Forgot Password</h1>
              <p className="text-sm text-[#f1e0c5]/60 mb-6">We'll email you a 6-digit code to reset it.</p>
              <form onSubmit={requestCode} className="space-y-4">
                <div>
                  <Label htmlFor="fp-email" className="text-xs uppercase tracking-wider text-[#f1e0c5]/60">Email</Label>
                  <Input data-testid="forgot-password-email-input" id="fp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 bg-black/30 border-white/10" />
                </div>
                <Button data-testid="forgot-password-send-code-button" type="submit" disabled={loading} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-sm">
                  {loading ? "Sending…" : "Send Reset Code"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h1 className="font-display text-3xl mb-2">Enter Code</h1>
              <p className="text-sm text-[#f1e0c5]/60 mb-6">Check {email} for your 6-digit code — it expires in 10 minutes.</p>
              <form onSubmit={resetPassword} className="space-y-4">
                <Input
                  data-testid="forgot-password-code-input"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="bg-black/30 border-white/10 text-center text-2xl tracking-[0.4em]"
                />
                <Input
                  data-testid="forgot-password-new-password-input"
                  type="password"
                  placeholder="New password (min. 8 characters)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-black/30 border-white/10"
                />
                <Input
                  data-testid="forgot-password-confirm-password-input"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-black/30 border-white/10"
                />
                <Button data-testid="forgot-password-reset-submit-button" type="submit" disabled={loading} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-sm">
                  {loading ? "Resetting…" : "Reset Password"}
                </Button>
                <button data-testid="forgot-password-resend-button" type="button" onClick={() => setStep("email")} className="w-full text-xs text-[#f1e0c5]/50 hover:text-[#D4AF37]">
                  Use a different email / resend code
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
